import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { StickerArtCatalog } from "@tento-ui/components/sticker/sticker-art-catalog";
import { StickerCutoutPreview } from "@tento-ui/components/sticker/sticker-cutout-preview";
import { StickerEffect } from "@tento-ui/components/sticker/sticker-effect";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import type { StickerSlot } from "@tento-lona/sheets-ui/platform";

@component({ name: "lona-sticker-details" })
export class StickerDetails extends LonaWebComponent {
  private readonly $deleteBtn: Button = this.hydrate("delete");
  private readonly $grid: HTMLElement = this.$("grid");
  private readonly $actions: HTMLElement = this.$("actions");

  private readonly tiles: StickerDetails.Tile[] = [];
  private onPickArt: Option<(art: string) => void> = null;
  private onDelete: Option<() => void> = null;
  private selectedArt: Option<string> = null;
  private tileSizePx: Option<number> = null;

  constructor() {
    super();

    GESTURE_MANAGER.addPointerEvent(this.$deleteBtn, {
      onClick: () => this.onDelete?.(),
    });

    void this.loadArtTiles();
  }

  bindCreate(props: StickerDetails.CreateProps): this {
    this.onPickArt = props.onPickArt;
    this.onDelete = null;
    this.selectedArt = null;
    this.setActionsVisible(false);
    this.syncSelection();
    return this;
  }

  bindDetails(props: StickerDetails.DetailsProps): this {
    this.onPickArt = props.onPickArt;
    this.onDelete = props.onDelete;
    this.selectedArt = props.slot.stickerId;
    this.setActionsVisible(true);
    this.syncSelection();
    return this;
  }

  private pickArt(art: string): void {
    this.selectedArt = art;
    this.syncSelection();
    this.onPickArt?.(art);
  }

  private syncSelection(): void {
    for (const tile of this.tiles) {
      const selected = tile.asset.id === this.selectedArt;
      tile.$button.toggleAttribute("selected", selected);
      tile.$button.setAttribute(
        "aria-pressed",
        selected ? "true" : "false",
      );
    }
  }

  private setActionsVisible(visible: boolean): void {
    this.$actions.style.display = visible ? "" : "none";
  }

  private async loadArtTiles(): Promise<void> {
    const assets = await StickerArtCatalog.assets().catch(
      (): StickerArtCatalog.Asset[] => [],
    );
    for (const asset of assets) {
      const tile = this.makeArtTile(asset);
      this.tiles.push(tile);
      this.$grid.appendChild(tile.$button);
    }
    this.syncSelection();
  }

  private bindPreview(tile: StickerDetails.Tile): void {
    const { asset, $preview } = tile;
    void StickerArtCatalog.cutout(asset.id).then((result) => {
      if (!result) return;
      this.applyTileSize(
        StickerCutoutPreview.resolveTileSize(result, {
          width: asset.displayWidth,
          height: asset.displayHeight,
        }),
      );
      $preview.bindResult({
        result,
        alt: asset.alt,
        depth: "flat",
        displayWidth: asset.displayWidth,
        displayHeight: asset.displayHeight,
        effect: StickerEffect.DEFAULT,
        effectVariant: null,
        imageRendering: asset.imageRendering ?? "pixelated",
      });
    });
  }

  private makeArtTile(asset: StickerArtCatalog.Asset): StickerDetails.Tile {
    const $button = document.createElement("button");
    $button.type = "button";
    $button.className = "art-tile";
    $button.setAttribute("aria-label", asset.label);

    const $preview = StickerCutoutPreview.make();
    $button.appendChild($preview);
    const tile = { asset, $button, $preview };
    this.bindPreview(tile);

    GESTURE_MANAGER.addPointerEvent($button, {
      onClick: () => this.pickArt(asset.id),
    });
    return tile;
  }

  private applyTileSize(tileSizePx: number): void {
    if (this.tileSizePx != null && this.tileSizePx >= tileSizePx) return;
    this.tileSizePx = tileSizePx;
    this.style.setProperty("--sticker-picker-tile-size", `${tileSizePx}px`);
  }

  /** Compatibility for older call sites while create/details converge on
   *  the prop-object bind methods above. */
  bind(
    slot: StickerSlot,
    onDelete: () => void,
  ): this {
    return this.bindDetails({
      slot,
      onPickArt: () => {},
      onDelete,
    });
  }

  static override $styles: Style.Compat[] = [
    Button.$style,
    css`
      :host {
        display: block;
        width: 100%;
        --sticker-picker-gap: 8px;
        --sticker-picker-padding: 12px;
        --sticker-picker-tile-size: 91px;
      }

      #root {
        padding: 0px;
        gap: 0px;
      }

      #grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, var(--sticker-picker-tile-size));
        justify-content: space-evenly;
        gap: var(--sticker-picker-gap);
        max-height: min(70vh, 472px);
        padding: var(--sticker-picker-padding) var(--sticker-picker-padding)
          var(--sticker-picker-gap);
        overflow-y: auto;
        overflow-x: hidden;
      }

      .art-tile {
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--sticker-picker-tile-size);
        height: var(--sticker-picker-tile-size);
        padding: 0px;
        box-sizing: border-box;
        border: 0;
        border-radius: 10px;
        background: transparent;
        cursor: pointer;
      }

      .art-tile:hover,
      .art-tile[selected] {
        background: color-mix(in srgb, var(--hover-color) 70%, var(--bg) 30%);
      }

      .art-tile std-sticker-cutout-preview {
        pointer-events: none;
      }

      #grid,
      #actions {
        width: 100%;
        box-sizing: border-box;
      }

      #actions {
        padding: 0px var(--sticker-picker-padding)
          var(--sticker-picker-padding);
      }

      #delete {
        width: 100%;
      }
    `,
  ];

  static override $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <div id="grid"></div>
      <div id="actions">
        <std-button id="delete" destructive>Delete</std-button>
      </div>
    </std-col>
  `;
}

export namespace StickerDetails {
  export type Tile = {
    asset: StickerArtCatalog.Asset;
    $button: HTMLButtonElement;
    $preview: StickerCutoutPreview;
  };

  export type CreateProps = {
    onPickArt: (art: string) => void;
  };

  export type DetailsProps = {
    slot: StickerSlot;
    onPickArt: (art: string) => void;
    onDelete: () => void;
  };
}
